import { CoButton } from "components/CoButton";
import Tag from "components/Tag";
import { ThreadInteraction } from "features/Thread/types";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import useThread from "hooks/useThread";
import { Filter, Filters } from "store/slices/search";
import { getFilterLabels, handleRemoveFilter } from "../utils/filterLabels";

type FilterTagProps = {
  label: string;
  onClick: () => void;
};

const FilterTag = ({ label, onClick }: FilterTagProps) => (
  <Tag
    label={label}
    icon="icon-x"
    size="sm"
    className="cursor-pointer"
    onClick={onClick}
  />
);

type ApplyAndResendFooterProps = {
  areFiltersChanged: boolean;
  interaction: ThreadInteraction;
  onClose: () => void;
  onFilterChange: <T extends Filter>(filter: T, value: Filters[T]) => void;
  pendingFilterCount: number;
  pendingFilters: Filters;
  threadId: string;
  onClearAllFilters: () => void;
};

function ApplyAndResendFooter({
  areFiltersChanged,
  interaction,
  onClose,
  onFilterChange,
  pendingFilterCount,
  pendingFilters,
  threadId,
  onClearAllFilters,
}: ApplyAndResendFooterProps) {
  const [labels] = useLabels("filter");

  const { addInteractionToThread } = useThread(threadId);
  const { currentSearchMode } = useSearchMode();

  const onApplyAndResend = () => {
    addInteractionToThread({
      userMessage: interaction.user_message,
      filters: pendingFilters,
      actionSource: MixpanelActionSource.ApplyFiltersAndResend,
      searchMode: currentSearchMode,
      onContinueOrRedirect: () => {
        onClose();
      },
    });
  };

  // Modify a single filter value
  const changeFilter = <T extends keyof Filters>(
    filter: T,
    value: Filters[T]
  ) => {
    onFilterChange(
      filter,
      Array.isArray(value) && value.length === 0 ? undefined : value
    );
  };

  // Remove a single filter
  const removeFilter = (filter: Filter) => {
    onFilterChange(filter, undefined);
  };

  const onTagClicked = (
    key: keyof Filters,
    value: string | number | undefined
  ) => {
    handleRemoveFilter(key, value, pendingFilters, changeFilter, removeFilter);
  };

  const filterLabels = getFilterLabels(pendingFilters);

  return (
    <div className="flex flex-col gap-4 justify-between w-full">
      {pendingFilterCount > 0 && (
        <div className="flex flex-wrap gap-1">
          {pendingFilterCount > 1 && (
            <FilterTag
              label={`${labels["clear-all"]} (${pendingFilterCount})`}
              onClick={onClearAllFilters}
            />
          )}
          {filterLabels.map(({ label, key, value }) => (
            <FilterTag
              key={label}
              label={label}
              onClick={() => onTagClicked(key, value)}
            />
          ))}
        </div>
      )}
      <p className="line-clamp-5">{interaction.user_message}</p>
      <CoButton
        onClick={onApplyAndResend}
        disabled={!areFiltersChanged}
        size="sm"
        icon="icon-arrow-up"
        label={labels["apply-and-resend"]}
      />
    </div>
  );
}

export default ApplyAndResendFooter;
